import { useState, useEffect, SetStateAction } from 'react';
import {
  AppSettings,
  TravelItem,
  WorkItem,
  PartItem,
  CustomLineItem,
  CalculatedTotals,
  SavedOffer,
  AddressType
} from '../types';
import { useTranslation } from 'react-i18next';
import {
  formatCurrency,
  generateOfferNumber,
  generateJobNumber,
  saveOffer,
  updateOffer,
  loadOffers
} from '../utils';
import { calculateDistance, DistanceMatrixResult } from '../utils/distanceMatrix';
import { Confetti, useConfetti } from './Confetti';
import { AddressAutocomplete } from './AddressAutocomplete';
import { useGoogleMapsLoaded } from './GoogleMapsProvider';

interface CalculatorProps {
  settings: AppSettings;
  customerName: string;
  setCustomerName: (value: string) => void;
  customerAddress: string;
  setCustomerAddress: (value: string) => void;
  customerNotes: string;
  setCustomerNotes: (value: string) => void;
  addressType: AddressType;
  setAddressType: (value: AddressType) => void;
  travelItems: TravelItem[];
  setTravelItems: (value: SetStateAction<TravelItem[]>) => void;
  workItems: WorkItem[];
  setWorkItems: (value: SetStateAction<WorkItem[]>) => void;
  parts: PartItem[];
  setParts: (value: SetStateAction<PartItem[]>) => void;
  customLines: CustomLineItem[];
  setCustomLines: (value: SetStateAction<CustomLineItem[]>) => void;
  calculatedTotals: CalculatedTotals;
  editingOfferId: string | null;
  jobNumber: string;
  setJobNumber: (value: string) => void;
  onClear: () => void;
  onSaved: () => void;
  onPrint: (offer: SavedOffer) => void;
  onCreateWorkOrder: (offer: SavedOffer) => void;
}

const Calculator = ({
  settings,
  customerName,
  setCustomerName,
  customerAddress,
  setCustomerAddress,
  customerNotes,
  setCustomerNotes,
  addressType,
  setAddressType,
  travelItems,
  setTravelItems,
  workItems,
  setWorkItems,
  parts,
  setParts,
  customLines,
  setCustomLines,
  calculatedTotals,
  editingOfferId,
  jobNumber,
  setJobNumber,
  onClear,
  onSaved,
  onPrint,
  onCreateWorkOrder
}: CalculatorProps) => {
  const { t } = useTranslation();
  const { showConfetti, triggerConfetti, handleComplete } = useConfetti();
  const { isLoaded: isMapsLoaded } = useGoogleMapsLoaded();

  // State for distance calculation
  const [calculatingDistanceId, setCalculatingDistanceId] = useState<number | null>(null);
  const [distanceError, setDistanceError] = useState<{ id: number; message: string } | null>(null);

  // State for job number validation
  const [jobNumberError, setJobNumberError] = useState<string | null>(null);

  // Clear job number error when job number changes
  useEffect(() => {
    if (jobNumber) {
      setJobNumberError(null);
    }
  }, [jobNumber]);

  // Generate job number handler
  const handleGenerateJobNumber = () => {
    const newJobNumber = generateJobNumber(settings.userIdentityNumber);
    setJobNumber(newJobNumber);
  };

  // Validate job number before save/print
  const validateJobNumber = (): boolean => {
    if (!jobNumber.trim()) {
      setJobNumberError(t('calculator.jobNumberRequired'));
      return false;
    }
    setJobNumberError(null);
    return true;
  };

  // Check if distance calculation is possible
  const canCalculateDistance = isMapsLoaded && 
    !!settings.googleMapsApiKey && 
    !!settings.serviceBranchAddress && 
    !!customerAddress;

  // Handle distance calculation for a travel item
  const handleCalculateDistance = async (itemId: number) => {
    if (!canCalculateDistance) return;

    setCalculatingDistanceId(itemId);
    setDistanceError(null);

    try {
      const result: DistanceMatrixResult = await calculateDistance(
        settings.serviceBranchAddress!,
        customerAddress
      );
      
      // Update the travel item with one-way distance and time
      // (tripMultiplier will be applied in cost calculation)
      updateTravelItem(itemId, 'distance', result.distanceKm);
      updateTravelItem(itemId, 'travelTime', Math.round(result.durationMinutes / 60 * 10) / 10); // Convert to hours, round to 1 decimal
    } catch (error) {
      setDistanceError({
        id: itemId,
        message: error instanceof Error ? error.message : 'Failed to calculate distance'
      });
    } finally {
      setCalculatingDistanceId(null);
    }
  };

  // === TRAVEL ITEMS ===
  const addTravelItem = () => {
    const newItem: TravelItem = {
      id: Date.now(),
      mode: 'perKm',
      distance: 0,
      travelTime: 0,
      timeRate: settings.defaultTimeRate,
      distanceRate: settings.defaultDistanceRate,
      clientsOnTrip: 1,
      discountPercent: 0,
      fixedPrice: 0,
      tripMultiplier: 1, // Default: one-way
    };
    setTravelItems([...travelItems, newItem]);
  };

  const updateTravelItem = (id: number, field: keyof TravelItem, value: number | string) => {
    setTravelItems((prevItems: TravelItem[]) => prevItems.map((item: TravelItem) =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeTravelItem = (id: number) => {
    setTravelItems((prevItems: TravelItem[]) => prevItems.filter((item: TravelItem) => item.id !== id));
  };

  // === WORK ITEMS ===
  const addWorkItem = () => {
    const newItem: WorkItem = {
      id: Date.now(),
      technicians: 1,
      hours: 0,
      hourlyRate: settings.defaultHourlyRate,
      callOutFee: 0,
      customCallOutFee: 0,
      nightSunday: false,
      urgent: false,
      accommodationNights: 0,
      perDiemDays: 0,
    };
    setWorkItems([...workItems, newItem]);
  };

  const updateWorkItem = (id: number, field: keyof WorkItem, value: number | boolean) => {
    setWorkItems((prevItems: WorkItem[]) => prevItems.map((item: WorkItem) =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeWorkItem = (id: number) => {
    setWorkItems((prevItems: WorkItem[]) => prevItems.filter((item: WorkItem) => item.id !== id));
  };

  // === PARTS ===
  const addPart = () => {
    const newPart: PartItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      markup: 15,
    };
    setParts([...parts, newPart]);
  };

  const updatePart = (id: number, field: keyof PartItem, value: string | number) => {
    setParts((prevItems: PartItem[]) => prevItems.map((item: PartItem) =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removePart = (id: number) => {
    setParts((prevItems: PartItem[]) => prevItems.filter((item: PartItem) => item.id !== id));
  };

  // === CUSTOM LINES ===
  const addCustomLine = () => {
    const newLine: CustomLineItem = {
      id: Date.now(),
      name: '',
      description: '',
      price: 0,
    };
    setCustomLines([...customLines, newLine]);
  };

  const updateCustomLine = (id: number, field: keyof CustomLineItem, value: string | number) => {
    setCustomLines((prevItems: CustomLineItem[]) => prevItems.map((item: CustomLineItem) =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeCustomLine = (id: number) => {
    setCustomLines((prevItems: CustomLineItem[]) => prevItems.filter((item: CustomLineItem) => item.id !== id));
  };

  // === SAVE/PRINT ===
  const handleSave = () => {
    // Validate job number
    if (!validateJobNumber()) {
      return;
    }

    const offerData: Omit<SavedOffer, 'id' | 'documentNumber' | 'createdAt' | 'updatedAt'> = {
      customerName,
      customerAddress,
      customerNotes,
      addressType,
      jobNumber,
      travelItems,
      workItems,
      parts,
      customLines,
      totals: calculatedTotals,
      settings: {
        printLanguage: settings.printLanguage,
        companyCountry: settings.companyCountry,
      },
    };

    if (editingOfferId) {
      const existingOffer = loadOffers().find(o => o.id === editingOfferId);
      if (existingOffer) {
        updateOffer({
          ...offerData,
          id: editingOfferId,
          documentNumber: existingOffer.documentNumber,
          createdAt: existingOffer.createdAt,
          updatedAt: new Date().toISOString(),
        });
      }
    } else {
      const newOffer: SavedOffer = {
        ...offerData,
        id: crypto.randomUUID(),
        documentNumber: generateOfferNumber(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      saveOffer(newOffer);
    }

    onSaved();
    triggerConfetti();
  };

  const handlePrint = () => {
    // Validate job number
    if (!validateJobNumber()) {
      return;
    }

    const offer: SavedOffer = {
      id: editingOfferId || crypto.randomUUID(),
      documentNumber: editingOfferId
        ? loadOffers().find(o => o.id === editingOfferId)?.documentNumber || generateOfferNumber()
        : generateOfferNumber(),
      customerName,
      customerAddress,
      customerNotes,
      addressType,
      jobNumber,
      travelItems,
      workItems,
      parts,
      customLines,
      totals: calculatedTotals,
      settings: {
        printLanguage: settings.printLanguage,
        companyCountry: settings.companyCountry,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onPrint(offer);
  };

  return (
    <div className="calculator-page">
      <Confetti active={showConfetti} onComplete={handleComplete} />
      {editingOfferId && (() => {
        const offer = loadOffers().find(o => o.id === editingOfferId);
        return offer ? (
          <div className="edit-banner">
            <span className="edit-banner-icon">✏️</span>
            <span className="edit-banner-text">
              {t('calculator.editingOffer') || 'Editing Offer'}: <strong>{offer.documentNumber}</strong>
              {offer.customerName && ` - ${offer.customerName}`}
            </span>
          </div>
        ) : null;
      })()}

      <div className="page-header">
        <h1>{t('calculator.title')}</h1>
      </div>

      <div className="calculator-layout">
        {/* Left Column - Forms */}
        <div className="calculator-forms">
          {/* Job Number Section - Prominent at top */}
          <section className="form-section form-section-prominent">
            <h2 className="section-title">
              🔢 {t('calculator.jobNumber')}
            </h2>
            <div className="btn-group" style={{ alignItems: 'flex-start' }}>
              <div className="form-group job-number-group">
                <input
                  type="text"
                  value={jobNumber}
                  onChange={(e) => setJobNumber(e.target.value)}
                  placeholder={t('calculator.jobNumberPlaceholder')}
                  className={`form-input input-job-number ${jobNumberError ? 'input-error' : ''}`}
                />
                {jobNumberError && (
                  <span className="error-message">
                    ⚠️ {jobNumberError}
                  </span>
                )}
              </div>
              <button 
                className="btn btn-secondary"
                onClick={handleGenerateJobNumber}
                type="button"
                style={{ height: '42px' }}
              >
                {t('calculator.generateJobNumber')}
              </button>
            </div>
          </section>

          {/* Customer Information */}
          <section className="form-section">
            <h2 className="section-title">{t('calculator.customer.title')}</h2>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label>{t('calculator.customer.name')}</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t('calculator.customer.namePlaceholder')}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>{t('calculator.customer.address')}</label>
                <AddressAutocomplete
                  value={customerAddress}
                  onChange={setCustomerAddress}
                  placeholder={t('calculator.customer.addressPlaceholder')}
                  className="form-input"
                />
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={addressType === 'billing' || addressType === 'both'}
                      onChange={(e) => {
                        const isSiteChecked = addressType === 'site' || addressType === 'both';
                        if (e.target.checked) {
                          setAddressType(isSiteChecked ? 'both' : 'billing');
                        } else {
                          setAddressType(isSiteChecked ? 'site' : 'billing');
                        }
                      }}
                    />
                    {t('calculator.customer.billingAddress')}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={addressType === 'site' || addressType === 'both'}
                      onChange={(e) => {
                        const isBillingChecked = addressType === 'billing' || addressType === 'both';
                        if (e.target.checked) {
                          setAddressType(isBillingChecked ? 'both' : 'site');
                        } else {
                          setAddressType(isBillingChecked ? 'billing' : 'site');
                        }
                      }}
                    />
                    {t('calculator.customer.serviceSite')}
                  </label>
                </div>
                <small className="form-hint">{t('calculator.customer.addressTypeHint')}</small>
              </div>
            </div>
            <div className="form-group">
              <label>{t('calculator.customer.notes')}</label>
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                placeholder={t('calculator.customer.notesPlaceholder')}
                className="form-input"
                rows={2}
              />
            </div>
          </section>

          {/* Custom Lines Section */}
          <section className="form-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">{t('calculator.customLines.title')}</h2>
                <p className="section-subtitle">{t('calculator.customLines.subtitle')}</p>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={addCustomLine}>
                {t('calculator.customLines.addButton')}
              </button>
            </div>

            {customLines.map((line, index) => (
              <div key={line.id} className="item-card">
                <div className="item-header">
                  <span className="item-label">Custom Item #{index + 1}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => removeCustomLine(line.id)}
                  >
                    {t('calculator.customLines.removeButton')}
                  </button>
                </div>
                <div className="form-row form-row-4">
                  <div className="form-group">
                    <label>{t('calculator.customLines.name')}</label>
                    <input
                      type="text"
                      value={line.name}
                      onChange={(e) => updateCustomLine(line.id, 'name', e.target.value)}
                      placeholder={t('calculator.customLines.namePlaceholder')}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.customLines.description')}</label>
                    <input
                      type="text"
                      value={line.description}
                      onChange={(e) => updateCustomLine(line.id, 'description', e.target.value)}
                      placeholder={t('calculator.customLines.descriptionPlaceholder')}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.customLines.price')}</label>
                    <input
                      type="number"
                      value={line.price}
                      onChange={(e) => updateCustomLine(line.id, 'price', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      step={1}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.travel.discount')}</label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        value={line.discountPercent || 0}
                        onChange={(e) => updateCustomLine(line.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min={0}
                        max={100}
                      />
                      <span className="input-suffix">%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Travel Section */}
          <section className="form-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  {t('calculator.travel.title')}
                  {calculatedTotals.breakdown.travel > 0 && (
                    <span className="section-price">{formatCurrency(calculatedTotals.breakdown.travel, settings.companyCountry)}</span>
                  )}
                </h2>
                <p className="section-subtitle">{t('calculator.travel.subtitle')}</p>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={addTravelItem}>
                {t('calculator.travel.addButton')}
              </button>
            </div>

            {travelItems.map((item, index) => (
              <div key={item.id} className="item-card">
                <div className="item-header">
                  <span className="item-label">{t('calculator.travel.itemLabel')} #{index + 1}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => removeTravelItem(item.id)}
                  >
                    {t('calculator.travel.removeButton')}
                  </button>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>{t('calculator.travel.mode')}</label>
                    <select
                      value={item.mode}
                      onChange={(e) => updateTravelItem(item.id, 'mode', e.target.value)}
                      className="form-input"
                    >
                      <option value="perKm">{t('calculator.travel.modePerKm')}</option>
                      <option value="fixed">{t('calculator.travel.modeFixed')}</option>
                    </select>
                  </div>

                  {item.mode === 'fixed' ? (
                    <>
                      <div className="form-group">
                        <label>{t('calculator.travel.fixedPrice')}</label>
                        <input
                          type="number"
                          value={item.fixedPrice}
                          onChange={(e) => updateTravelItem(item.id, 'fixedPrice', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min={0}
                          step={10}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Calculate button row */}
                      <div className="form-group grid-span-3">
                        <div className="calculate-distance-row">
                          <button
                            className="btn btn-secondary btn-sm calculate-distance-btn"
                            onClick={() => handleCalculateDistance(item.id)}
                            disabled={!canCalculateDistance || calculatingDistanceId === item.id}
                            title={
                              !settings.googleMapsApiKey 
                                ? t('calculator.travel.noApiKey') || 'Google Maps API key not configured'
                                : !settings.serviceBranchAddress 
                                  ? t('calculator.travel.noBranchAddress')
                                  : !customerAddress 
                                    ? t('calculator.travel.noCustomerAddress')
                                    : t('calculator.travel.calculateDistance')
                            }
                          >
                            {calculatingDistanceId === item.id ? (
                              <>
                                <span className="spinner" style={{ marginRight: '6px' }}>⏳</span>
                                {t('calculator.travel.calculating')}
                              </>
                            ) : (
                              <>📍 {t('calculator.travel.calculateDistance')}</>
                            )}
                          </button>
                          {distanceError?.id === item.id && (
                            <span style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}>
                              ⚠️ {distanceError.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>{t('calculator.travel.travelTime')}</label>
                        <input
                          type="number"
                          value={item.travelTime}
                          onChange={(e) => updateTravelItem(item.id, 'travelTime', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min={0}
                          step={0.5}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('calculator.travel.timeRate')}</label>
                        <input
                          type="number"
                          value={item.timeRate}
                          onChange={(e) => updateTravelItem(item.id, 'timeRate', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min={0}
                          step={5}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('calculator.travel.distance')}</label>
                        <input
                          type="number"
                          value={item.distance}
                          onChange={(e) => updateTravelItem(item.id, 'distance', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min={0}
                          step={10}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('calculator.travel.distanceRate')}</label>
                        <input
                          type="number"
                          value={item.distanceRate}
                          onChange={(e) => updateTravelItem(item.id, 'distanceRate', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min={0}
                          step={0.05}
                        />
                      </div>
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={item.tripMultiplier === 2}
                            onChange={(e) => updateTravelItem(item.id, 'tripMultiplier', e.target.checked ? 2 : 1)}
                          />
                          {t('calculator.travel.roundTrip')}
                        </label>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>{t('calculator.travel.discount')}</label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        value={item.discountPercent}
                        onChange={(e) => updateTravelItem(item.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min={0}
                        max={100}
                      />
                      <span className="input-suffix">%</span>
                    </div>
                  </div>

                  {/* Travel description - free text for print */}
                  <div className="form-group grid-span-full">
                    <label>{t('calculator.travel.description')}</label>
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => updateTravelItem(item.id, 'description', e.target.value)}
                      placeholder={t('calculator.travel.descriptionPlaceholder')}
                      className="form-input"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Labor Section */}
          <section className="form-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  {t('calculator.labor.title')}
                  {(calculatedTotals.breakdown.labor + calculatedTotals.breakdown.accommodation + calculatedTotals.breakdown.callOutFee) > 0 && (
                    <span className="section-price">{formatCurrency(calculatedTotals.breakdown.labor + calculatedTotals.breakdown.accommodation + calculatedTotals.breakdown.callOutFee, settings.companyCountry)}</span>
                  )}
                </h2>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={addWorkItem}>
                {t('calculator.labor.addButton')}
              </button>
            </div>

            {workItems.map((item, index) => (
              <div key={item.id} className="item-card">
                <div className="item-header">
                  <span className="item-label">{t('calculator.labor.itemLabel')} #{index + 1}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => removeWorkItem(item.id)}
                  >
                    {t('calculator.labor.removeButton')}
                  </button>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>{t('calculator.labor.technicians')}</label>
                    <input
                      type="number"
                      value={item.technicians}
                      onChange={(e) => updateWorkItem(item.id, 'technicians', parseInt(e.target.value) || 1)}
                      className="form-input"
                      min={1}
                      max={10}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.labor.hours')}</label>
                    <input
                      type="number"
                      value={item.hours}
                      onChange={(e) => updateWorkItem(item.id, 'hours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      step={0.5}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.labor.hourlyRate')}</label>
                    <input
                      type="number"
                      value={item.hourlyRate}
                      onChange={(e) => updateWorkItem(item.id, 'hourlyRate', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      step={5}
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label>{t('calculator.labor.callOutFee')}</label>
                    <select
                      value={item.callOutFee}
                      onChange={(e) => updateWorkItem(item.id, 'callOutFee', parseInt(e.target.value))}
                      className="form-input"
                    >
                      <option value={0}>No call-out fee</option>
                      <option value={50}>€50</option>
                      <option value={75}>€75</option>
                      <option value={100}>€100</option>
                      <option value={150}>€150</option>
                      <option value={-1}>Custom</option>
                    </select>
                  </div>
                  {item.callOutFee === -1 && (
                    <div className="form-group">
                      <label>{t('calculator.labor.customCallOutFee')}</label>
                      <input
                        type="number"
                        value={item.customCallOutFee}
                        onChange={(e) => updateWorkItem(item.id, 'customCallOutFee', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min={0}
                      />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>{t('calculator.labor.surcharges')}</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={item.nightSunday}
                        onChange={(e) => updateWorkItem(item.id, 'nightSunday', e.target.checked)}
                      />
                      <span>{t('calculator.labor.nightSunday')}</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={item.urgent}
                        onChange={(e) => updateWorkItem(item.id, 'urgent', e.target.checked)}
                      />
                      <span>{t('calculator.labor.urgent')}</span>
                    </label>
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label>{t('calculator.labor.accommodation')}</label>
                    <input
                      type="number"
                      value={item.accommodationNights}
                      onChange={(e) => updateWorkItem(item.id, 'accommodationNights', parseInt(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.labor.perDiem')}</label>
                    <input
                      type="number"
                      value={item.perDiemDays}
                      onChange={(e) => updateWorkItem(item.id, 'perDiemDays', parseInt(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label>{t('calculator.discount')} (%)</label>
                    <input
                      type="number"
                      value={item.discountPercent || 0}
                      onChange={(e) => updateWorkItem(item.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Parts Section */}
          <section className="form-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  {t('calculator.parts.title')}
                  {calculatedTotals.breakdown.parts > 0 && (
                    <span className="section-price">{formatCurrency(calculatedTotals.breakdown.parts, settings.companyCountry)}</span>
                  )}
                </h2>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={addPart}>
                {t('calculator.parts.addButton')}
              </button>
            </div>

            {parts.map((part, index) => (
              <div key={part.id} className="item-card">
                <div className="item-header">
                  <span className="item-label">Part #{index + 1}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => removePart(part.id)}
                  >
                    {t('calculator.parts.removeButton')}
                  </button>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group grid-span-2">
                    <label>{t('calculator.parts.description')}</label>
                    <input
                      type="text"
                      value={part.description}
                      onChange={(e) => updatePart(part.id, 'description', e.target.value)}
                      placeholder={t('calculator.parts.descriptionPlaceholder')}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>{t('calculator.parts.quantity')}</label>
                    <input
                      type="number"
                      value={part.quantity}
                      onChange={(e) => updatePart(part.id, 'quantity', parseInt(e.target.value) || 1)}
                      className="form-input"
                      min={1}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.parts.unitPrice')}</label>
                    <input
                      type="number"
                      value={part.unitPrice}
                      onChange={(e) => updatePart(part.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.parts.markup')}</label>
                    <input
                      type="number"
                      value={part.markup}
                      onChange={(e) => updatePart(part.id, 'markup', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      max={100}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.discount')} (%)</label>
                    <input
                      type="number"
                      value={part.discountPercent || 0}
                      onChange={(e) => updatePart(part.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>
              </div>
            ))}
          </section>
        </div>

        {/* Right Column - Summary */}
        <div className="calculator-summary">
          <div className="summary-card sticky">
            <h2 className="section-title">{t('calculator.summary.title')}</h2>

            <div className="summary-rows">
              {calculatedTotals.breakdown.customLines > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.customLines')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.customLines, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.travel > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.travel')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.travel, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.labor > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.labor')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.labor, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.callOutFee > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.callOutFee')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.callOutFee, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.accommodation > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.accommodation')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.accommodation, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.parts > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.parts')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.parts, settings.companyCountry)}</span>
                </div>
              )}
            </div>

            <hr />

            <div className="summary-row">
              <span>{t('calculator.summary.subtotal')}</span>
              <span className="summary-value">{formatCurrency(calculatedTotals.subtotal, settings.companyCountry)}</span>
            </div>

            <div className="summary-row">
              <span>{t('calculator.summary.vat')} ({calculatedTotals.vatPercent}%)</span>
              <span>{formatCurrency(calculatedTotals.vat, settings.companyCountry)}</span>
            </div>

            <div className="summary-row total">
              <span>{t('calculator.summary.total')}</span>
              <span className="summary-total">{formatCurrency(calculatedTotals.total, settings.companyCountry)}</span>
            </div>

            <div className="summary-actions">
              <button className="btn btn-secondary" onClick={onClear}>
                {t('calculator.actions.clearForm')}
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editingOfferId ? t('calculator.actions.updateQuote') : t('calculator.actions.saveQuote')}
              </button>
              <button className="btn btn-accent" onClick={handlePrint}>
                {t('calculator.actions.print')}
              </button>
              {/* Create Work Order button - only shown after quote is saved */}
              {editingOfferId && (
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    // Validate job number first
                    if (!validateJobNumber()) {
                      return;
                    }
                    // Get the current offer data
                    const existingOffer = loadOffers().find(o => o.id === editingOfferId);
                    if (existingOffer) {
                      // Create offer object with current form data
                      const offer: SavedOffer = {
                        ...existingOffer,
                        customerName,
                        customerAddress,
                        customerNotes,
                        addressType,
                        jobNumber,
                        travelItems,
                        workItems,
                        parts,
                        customLines,
                        totals: calculatedTotals,
                      };
                      onCreateWorkOrder(offer);
                    }
                  }}
                  style={{ 
                    background: 'var(--halton-accent-green, #7AC143)', 
                    color: 'white',
                    borderColor: 'var(--halton-accent-green, #7AC143)'
                  }}
                >
                  📋 {t('calculator.createWorkOrder')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;

